/**
 * HTTP Basic Auth dialog and fetch helper for protected splat files.
 */

/**
 * Show a login dialog for HTTP Basic Auth.
 * Returns credentials or null if the user cancels.
 */
const showAuthDialog = (realm: string, error?: string): Promise<{ username: string; password: string } | null> => {
    return new Promise((resolve) => {
        const overlay = document.createElement('div');
        overlay.style.cssText = [
            'position:fixed', 'inset:0', 'background:rgba(0,0,0,0.75)',
            'display:flex', 'align-items:center', 'justify-content:center',
            'z-index:9999', 'font-family:"Open Sans",sans-serif'
        ].join(';');

        const panel = document.createElement('div');
        panel.style.cssText = [
            'background:#1c1c1f', 'border:1px solid #3a3a40',
            'border-radius:6px', 'padding:24px 28px', 'width:300px',
            'box-shadow:0 8px 32px rgba(0,0,0,0.5)'
        ].join(';');

        panel.innerHTML = `
            <div style="display:flex;align-items:center;gap:10px;margin-bottom:16px">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#e0a030" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <span style="color:#e0e0e0;font-size:15px;font-weight:600">Acceso Restringido</span>
            </div>
            <p style="color:#888;font-size:12px;margin:0 0 ${error ? 8 : 16}px">${realm}</p>
            ${error ? `<p style="color:#e05050;font-size:12px;margin:0 0 14px">⚠ ${error}</p>` : ''}
            <label style="display:block;color:#aaa;font-size:11px;margin-bottom:4px;text-transform:uppercase;letter-spacing:.5px">Usuario</label>
            <input id="ss-user" type="text" autocomplete="username"
                style="width:100%;box-sizing:border-box;background:#2a2a2e;border:1px solid #444;color:#e0e0e0;padding:7px 10px;border-radius:4px;font-size:13px;margin-bottom:12px;outline:none">
            <label style="display:block;color:#aaa;font-size:11px;margin-bottom:4px;text-transform:uppercase;letter-spacing:.5px">Contraseña</label>
            <input id="ss-pass" type="password" autocomplete="current-password"
                style="width:100%;box-sizing:border-box;background:#2a2a2e;border:1px solid #444;color:#e0e0e0;padding:7px 10px;border-radius:4px;font-size:13px;margin-bottom:20px;outline:none">
            <div style="display:flex;gap:8px;justify-content:flex-end">
                <button id="ss-cancel"
                    style="padding:7px 16px;background:#2a2a2e;border:1px solid #555;color:#aaa;border-radius:4px;cursor:pointer;font-size:13px">
                    Cancelar
                </button>
                <button id="ss-ok"
                    style="padding:7px 16px;background:#3a7bd5;border:none;color:#fff;border-radius:4px;cursor:pointer;font-size:13px;font-weight:600">
                    Acceder
                </button>
            </div>
        `;

        overlay.appendChild(panel);
        document.body.appendChild(overlay);

        const userInput = panel.querySelector<HTMLInputElement>('#ss-user')!;
        const passInput = panel.querySelector<HTMLInputElement>('#ss-pass')!;
        const cancelBtn = panel.querySelector<HTMLButtonElement>('#ss-cancel')!;
        const okBtn = panel.querySelector<HTMLButtonElement>('#ss-ok')!;

        requestAnimationFrame(() => userInput.focus());

        const cleanup = () => document.body.removeChild(overlay);

        const submit = () => {
            const username = userInput.value;
            const password = passInput.value;
            cleanup();
            resolve(username ? { username, password } : null);
        };

        cancelBtn.addEventListener('click', () => { cleanup(); resolve(null); });
        okBtn.addEventListener('click', submit);
        passInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') submit(); });
        userInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') passInput.focus(); });
        overlay.addEventListener('click', (e) => { if (e.target === overlay) { cleanup(); resolve(null); } });
    });
};

/**
 * Fetch a URL, handling HTTP 401 by showing a credentials dialog.
 * Returns a File on success, or null if the user cancels.
 * Throws on non-401 HTTP errors.
 */
const fetchWithAuth = async (url: string, filename: string): Promise<File | null> => {
    let authHeader: string | null = null;
    let lastError: string | undefined;

    for (let attempt = 0; attempt < 3; attempt++) {
        const headers: Record<string, string> = {};
        if (authHeader) headers['Authorization'] = authHeader;

        const response = await fetch(url, { headers });

        if (response.ok) {
            const blob = await response.blob();
            return new File([blob], filename, { type: blob.type });
        }

        if (response.status === 401) {
            const wwwAuth = response.headers.get('WWW-Authenticate') ?? '';
            const realmMatch = wwwAuth.match(/realm="([^"]+)"/i);
            const realm = realmMatch?.[1] ?? new URL(url, window.location.href).hostname;

            const credentials = await showAuthDialog(realm, lastError);
            if (!credentials) return null;

            authHeader = 'Basic ' + btoa(`${credentials.username}:${credentials.password}`);
            lastError = 'Credenciales incorrectas. Intenta de nuevo.';
        } else {
            throw new Error(`HTTP error ${response.status}`);
        }
    }

    throw new Error('Número máximo de intentos alcanzado');
};

/**
 * Check if a URL requires authentication (via a lightweight HEAD request).
 * Returns true if the server responds with 401.
 */
const requiresAuth = async (url: string): Promise<boolean> => {
    try {
        const response = await fetch(url, { method: 'HEAD' });
        return response.status === 401;
    } catch {
        return false;
    }
};

export { fetchWithAuth, requiresAuth };
